import {
  Box,
  Checkbox,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Typography,
} from '@mui/material';
import { StyledHoverTableRow } from '../../ui/StyledTable';
import { useTranslation } from 'react-i18next';
import { useEffect, useId, useRef, useState } from 'react';
import { equalsIgnoringCase } from '../../../utils/stringUtils';
import { formatInZonedTime } from '../../../utils/dateTime';
import useAuth from '../../../hooks/useAuth';
import SortableTableHead from '../../ui/SortableTableHead';

const getCellFomatter = (dataType) => {
  if (equalsIgnoringCase('date', dataType)) {
    return ({ row, field, dateFormat, timezone }) => {
      const value = row[field];
      return formatInZonedTime(value, dateFormat, timezone);
    };
  } if (equalsIgnoringCase('datetime', dataType)) {
    return ({ row, field, dateTimeFormat, timezone }) => {
      const value = row[field];
      return formatInZonedTime(value, dateTimeFormat, timezone);
    };
  }

  return ({ row, field }) => {
    if (typeof row[field] === 'boolean') {
      return `${row[field]}`;
    }

    return row[field] ?? '';
  };
};

/*
  columnMappingFormatter is a map from field to format function. Such as:
  {
    "action": ( { row, field } ) => '';
  }
*/
const SearchTable = ({
  dataItems = [],
  visibleColumns,
  isLoading,
  rowPerPage = 20,
  dense = true,
  stickyHeader = true,
  columnMappingFormatter,
  sortColumn,
  sortDirection,
  sortEvenHandler,
  columnHeaderMappingFormatter,
  selectedRows,
  handleSelectRow,
  handleSelectAllClick,
  showCheckbox,
  uniqueRecordIdentifier,
  isTableHeaderSticky = false,
  maxHeight = 600,
}) => {
  const { t } = useTranslation();

  const uniqueId = useId();
  const { dateFormat, dateTimeFormat, timezone } = useAuth();
  const viewportRef = useRef(null);
  const contentRef = useRef(null);
  const scrollbarRef = useRef(null);
  const spacerRef = useRef(null);
  const [needsHorizontalScroll, setNeedsHorizontalScroll] = useState(false);

  useEffect(() => {
    if (!isTableHeaderSticky) {
      return undefined;
    }

    const contentEl = contentRef.current;
    const spacerEl = spacerRef.current;
    if (!contentEl || !spacerEl) {
      return undefined;
    }

    const syncScrollbarSize = () => {
      const contentWidth = contentEl.scrollWidth;
      const visibleWidth = contentEl.parentElement?.clientWidth ?? 0;
      spacerEl.style.width = `${contentWidth}px`;
      setNeedsHorizontalScroll(contentWidth > visibleWidth);
    };

    syncScrollbarSize();

    const resizeObserver = new ResizeObserver(syncScrollbarSize);
    resizeObserver.observe(contentEl);
    window.addEventListener('resize', syncScrollbarSize);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener('resize', syncScrollbarSize);
    };
  }, [isTableHeaderSticky]);

  useEffect(() => {
    if (!isTableHeaderSticky) {
      return undefined;
    }

    const viewportEl = viewportRef.current;
    const scrollbarEl = scrollbarRef.current;
    if (!viewportEl || !scrollbarEl) {
      return undefined;
    }

    const handleWheel = (event) => {
      // Prefer genuine horizontal intent (trackpad swipe). Otherwise, treat
      // Shift+wheel as horizontal, since some browsers/OSes report that
      // gesture via `deltaY` rather than `deltaX`.
      let horizontalDelta = event.deltaX;
      if (horizontalDelta === 0 && event.shiftKey) {
        horizontalDelta = event.deltaY;
      }

      if (horizontalDelta === 0) {
        return;
      }

      const maxScrollLeft = scrollbarEl.scrollWidth - scrollbarEl.clientWidth;
      if (maxScrollLeft <= 0) {
        return;
      }

      event.preventDefault();
      scrollbarEl.scrollLeft = Math.min(
        Math.max(scrollbarEl.scrollLeft + horizontalDelta, 0),
        maxScrollLeft
      );
    };

    viewportEl.addEventListener('wheel', handleWheel, { passive: false });

    return () => {
      viewportEl.removeEventListener('wheel', handleWheel);
    };
  }, [isTableHeaderSticky]);

  const handleScrollbarScroll = (event) => {
    if (contentRef.current) {
      contentRef.current.style.left = `-${event.currentTarget.scrollLeft}px`;
    }
  };

  const tableHead = (
    <SortableTableHead
      columns={visibleColumns}
      sortBy={sortColumn}
      sortDirection={sortDirection}
      onRequestSort={sortEvenHandler}
      customColumn={columnHeaderMappingFormatter}
      handleSelectAllClick={handleSelectAllClick}
      showCheckbox={showCheckbox}
      numSelected={selectedRows?.length || 0}
      rowCount={dataItems?.length || 0}
    />
  );

  let tableRowsContent = (
    <>
      {Array.from(Array(rowPerPage).keys()).map((item) => (
        <TableRow hover key={`${uniqueId}-Row-busy-${item}`}>
          <TableCell colSpan={visibleColumns.length}>
            <Skeleton
              animation="wave"
              sx={{ height: '22px', borderRadius: '4px' }}
            />
          </TableCell>
        </TableRow>
      ))}
    </>
  );

  if (!isLoading) {
    if (dataItems.length === 0) {
      tableRowsContent = (
        <TableRow>
          <TableCell sx={{ textAlign: 'center' }} colSpan={visibleColumns.length}>
            <Typography variant="subtitle1">{t('Error_NoItemsFound')}</Typography>
          </TableCell>
        </TableRow>
      );
    } else {
      tableRowsContent = (
        <>
          {dataItems.map((rowItem, index) => {
            const rowKey = `${uniqueId}-Row-${index}`;
            return (
              <StyledHoverTableRow key={rowKey}>
                {(showCheckbox) && (
                  <TableCell padding="checkbox">
                    <Checkbox
                      color="primary"
                      checked={Array.isArray(selectedRows) && selectedRows.some((r) => (typeof r === 'object' ? r[uniqueRecordIdentifier] : r) === rowItem[uniqueRecordIdentifier])}
                      onChange={() => {
                        handleSelectRow(rowItem);
                      }}
                      inputProps={{ 'aria-label': 'select row' }}
                    />
                  </TableCell>
                )}
                {visibleColumns.map((col) => {
                  const cellKey = `${rowKey}-cell-${col.ColField}`;

                  let cellFormatter = null;
                  if (columnMappingFormatter) {
                    cellFormatter = columnMappingFormatter[col.ColField];
                  }

                  if (!cellFormatter) {
                    cellFormatter = getCellFomatter(col.DataType);
                  }
                  return (
                    <TableCell key={cellKey} sx={{ whiteSpace: 'nowrap' }}>
                      {cellFormatter({ row: rowItem, field: col.ColField, dateFormat, dateTimeFormat, timezone })}
                    </TableCell>
                  );
                })}
              </StyledHoverTableRow>
            );
          })}
        </>
      );
    }
  }

  const table = (
    <Table
      sx={{ minWidth: 750 }}
      size={dense ? 'small' : 'medium'}
      stickyHeader={stickyHeader}
    >
      {tableHead}
      <TableBody>{tableRowsContent}</TableBody>
    </Table>
  );

  if (!isTableHeaderSticky) {
    return (
      <TableContainer sx={{ maxHeight, width: '100%' }}>
        {table}
      </TableContainer>
    );
  }

  return (
    <Box sx={{ position: 'relative' }}>
      <Box ref={viewportRef} sx={{ overflow: 'clip' }}>
        <Box
          ref={contentRef}
          sx={{ width: 'max-content', minWidth: '100%', position: 'relative' }}
        >
          {table}
        </Box>
      </Box>
      <Box
        ref={scrollbarRef}
        onScroll={handleScrollbarScroll}
        sx={{
          overflowX: 'auto',
          overflowY: 'hidden',
          height: needsHorizontalScroll ? 14 : 0,
        }}
      >
        <Box ref={spacerRef} sx={{ height: 1 }} />
      </Box>
    </Box>
  );
};

export default SearchTable;
